import { hasUnusableNutrientInput, Ingredient, isIngredientReal, UnusableNutrientInput } from '@cf-shared/data-model/ingredient';

/**
 * Reporting for the nutrient-scaling guards in
 * `shared/src/data-model/ingredient.ts` (df-10a57222aae3).
 *
 * Those helpers turn a stored nutrient value they cannot compute with into zero,
 * and hand a typed-in ingredient's numbers back unscaled when its weight cannot
 * be multiplied by. Both are the right answer on screen -- the alternative is NaN
 * spreading through every total computed from it -- and both used to happen with
 * nothing anywhere recording that they had. `CLAUDE.md` asks for the opposite: a
 * damaged document is data to repair AND to report.
 *
 * `shared/` has no Sentry SDK and no reporting helper of its own, so the helpers
 * there collect what they skipped into an `UnusableNutrientInput` and a caller
 * that HAS a `LoggingService` reports it. This module is that step. It is shaped
 * after `orphan-subslot-reporter.ts`, which solves the same problem for a
 * different guard.
 */

// Structural minimum needed from LoggingService -- kept here so this module has zero
// Angular-DI dependencies and can be unit-tested under the ts-node ESM loader. The real
// LoggingService satisfies this shape.
export interface NutrientDataQualityReporter {
  trackSilentProblem(name: string, context: any): void;
}

/**
 * One stable Sentry name, so every occurrence groups into a single issue rather
 * than one issue per food. What distinguishes them goes in the context.
 */
const PROBLEM_NAME = 'nutrient-scaling: a stored nutrient value could not be used';

/**
 * Dedup for the life of the browser tab, keyed on call site plus the exact thing
 * that was wrong.
 *
 * This is not a nicety. The amount screen re-totals the whole meal on every
 * keystroke of the weight field, and redraws its nutrient list with it, so one
 * damaged value in a meal the user is editing would otherwise fire hundreds of
 * identical events in a minute. One event per distinct condition is enough to
 * find the upstream path that produced it; the rest is noise that would bury it.
 *
 * Cleared on tab reload, not on logout -- login granularity is not needed here,
 * and a second report of the same condition tells nobody anything new.
 */
const reportedConditions = new Set<string>();

/** Wipe the dedup memory. For tests only -- nothing in the app calls this. */
export function _resetNutrientDataQualityDedupForTesting(): void {
  reportedConditions.clear();
}

/**
 * Report whatever a scaling helper had to skip, at most once per distinct
 * condition.
 *
 * Safe to call with a collector nothing was recorded in -- that is the normal
 * case, and it returns without doing anything. So a caller can pass a collector
 * unconditionally and call this at the end of its pass.
 *
 * `callSite` names the code path in words that stay meaningful in a Sentry issue
 * months later ('amount-screen.meal-total'), not a file and line, which move.
 * `context` carries the ids that say WHICH food or meal it was.
 */
export function reportUnusableNutrientInput(
  unusable: UnusableNutrientInput,
  logging: NutrientDataQualityReporter,
  callSite: string,
  context?: Record<string, unknown>,
): void {
  if (!hasUnusableNutrientInput(unusable)) return;

  for (const nutrientKey of unusable.nutrientKeys) {
    if (!claimCondition(`${callSite}\u0000nutrient\u0000${nutrientKey}`)) continue;
    logging.trackSilentProblem(PROBLEM_NAME, {
      ...context,
      callSite,
      whatWasWrong: 'nutrient value',
      nutrientKey,
      ...describeSources(unusable.sourcesByNutrientKey?.[nutrientKey]),
    });
  }

  if (unusable.weightWasUnusable) {
    // the VALUE goes in the context rather than the key, because a missing weight and a
    // negative one are the same condition to whoever has to go and find the cause
    if (claimCondition(`${callSite}\u0000weight`)) {
      logging.trackSilentProblem(PROBLEM_NAME, {
        ...context,
        callSite,
        whatWasWrong: 'ingredient weight',
        weight_g: describeUnusableWeight(unusable.unusableWeight_g),
        ...describeSources(unusable.unusableWeightSources),
      });
    }
  }
}

/**
 * Report that there were no stored nutrients to scale at all, at most once per distinct
 * condition.
 *
 * The one damaged shape an `UnusableNutrientInput` cannot carry, which is why it is a
 * function of its own rather than something the collector records: the helpers note what
 * they skipped per nutrient KEY, and a map that is absent has no keys. It lives here, beside
 * `reportUnusableNutrientInput`, so that both halves of "the scaling rule was handed
 * something it could not use" share one Sentry name, one call-site field, and above all one
 * dedup -- a caller that reports this directly would file a fresh event on every redraw.
 *
 * Which document is damaged depends on the ingredient, and the caller knows: for a real
 * ingredient it is the FOOD record it points at, and for a typed-in one the ingredient
 * itself. Pass the ids in `context`.
 */
export function reportMissingNutrientMap(
  logging: NutrientDataQualityReporter,
  callSite: string,
  context?: Record<string, unknown>,
): void {
  if (!claimCondition(`${callSite}\u0000no-nutrient-map`)) return;
  logging.trackSilentProblem(PROBLEM_NAME, {
    ...context,
    callSite,
    whatWasWrong: 'no stored nutrients at all',
  });
}

/** True the first time this exact condition is seen, false every time after. */
function claimCondition(key: string): boolean {
  if (reportedConditions.has(key)) return false;
  reportedConditions.add(key);
  return true;
}

/**
 * The label a whole-meal caller passes to `mergeUnusableNutrientInput` for one ingredient, so
 * the report names it (#6322).
 *
 * A real ingredient is named by its food id, which is what someone triaging the report needs to
 * look the stored values up. A typed-in ingredient has no food record, so it is named by its
 * position only -- never by the name the user typed, which is their own text. `ingredientIndex`
 * is the 0-based position in the meal's ingredient list.
 */
export function describeIngredientForReport(ingredient: Ingredient, ingredientIndex: number): string {
  const where = `ingredient ${ingredientIndex}`;
  return isIngredientReal(ingredient) ? `${ingredient.foodId} (${where})` : `typed-in (${where})`;
}

/**
 * The `ingredients` context field: which ingredients held the bad value, as one string
 * Sentry shows readably (#6322). Nothing at all when the caller recorded no sources, so a
 * report from a single-ingredient call site looks exactly as it did.
 */
function describeSources(sources: string[] | undefined): { ingredients?: string } {
  return sources?.length ? { ingredients: sources.join(', ') } : {};
}

/**
 * A printable description of a weight that was not a usable number.
 *
 * Sentry drops an `undefined` context field, and `undefined` is the commonest
 * unusable weight there is -- a field missing from an old or damaged document. So
 * the field would vanish in exactly the case worth reading. A short string always
 * survives.
 */
function describeUnusableWeight(weight_g: unknown): string {
  if (weight_g === undefined) return 'undefined';
  if (weight_g === null) return 'null';
  if (typeof weight_g === 'number') return Number.isNaN(weight_g) ? 'NaN' : String(weight_g);
  return `${typeof weight_g}`;
}
